/**
 * Alshallal Pastry Company HR System - Module Guard Component
 * Phase 18.10: Route-level module visibility enforcement
 * 
 * Wraps page components to enforce module visibility policies.
 * Shows "Access Denied" if the module is disabled for the user's role.
 */
import React from 'react';
import { ShieldAlert, ArrowLeft, Briefcase, Loader2, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useModulePolicy } from '@/contexts/ModulePolicyContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { roleLabel } from '@/components/auth/ModeChooserDialog';

/**
 * Shown when a page is out of reach because the person chose Employee mode.
 * One button puts the hat back on and reloads the page they asked for.
 */
const EmployeeModeNotice = () => {
  const { isRTL } = useLanguage();
  const { actualRole, modeStepUpRequired, selectMode } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [asking, setAsking] = React.useState(false);
  const [error, setError] = React.useState(null);
  const label = roleLabel(actualRole, isRTL);

  const stepUp = async () => {
    if (modeStepUpRequired && !asking) {
      setAsking(true);
      return;
    }
    setBusy(true);
    setError(null);
    const result = await selectMode('role', { password });
    setBusy(false);
    if (!result.success) {
      setError(result.error);
      setPassword('');
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <Card className="max-w-md w-full" data-testid="employee-mode-notice">
        <CardHeader>
          <div className="mx-auto mb-2 w-fit rounded-full bg-slate-100 p-3">
            <Briefcase className="h-6 w-6 text-slate-600" />
          </div>
          <CardTitle className="text-center">
            {isRTL ? 'أنت في وضع الموظف' : "You're in Employee mode"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          <p className="text-sm text-muted-foreground">
            {isRTL
              ? `هذه الصفحة تتبع دورك كـ ${label}. بدّل الوضع للوصول إليها.`
              : `This page belongs to your ${label} role. Switch to reach it.`}
          </p>

          {asking && (
            <input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && password) stepUp(); }}
              placeholder={isRTL ? 'كلمة المرور' : 'Your password'}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              data-testid="employee-mode-password"
            />
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}

          <Button
            onClick={stepUp}
            disabled={busy || (asking && !password)}
            className="w-full"
            data-testid="employee-mode-switch-btn"
          >
            {busy
              ? <Loader2 className="me-2 h-4 w-4 animate-spin" />
              : <ShieldCheck className="me-2 h-4 w-4" />}
            {isRTL ? `التبديل إلى ${label}` : `Switch to ${label}`}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * ModuleGuard - Protects routes based on module visibility policy
 * @param {string|string[]} moduleKey - The module key(s) to check (e.g., 'employees', ['leave', 'leave_approvals'])
 * @param {React.ReactNode} children - The protected content to render if access is allowed
 * @param {React.ReactNode} fallback - Optional custom fallback component (defaults to Access Denied card)
 */
const ModuleGuard = ({ moduleKey, children, fallback }) => {
  const { isModuleEnabled, isAnyModuleEnabled, loading } = useModulePolicy();
  const { isRTL } = useLanguage();
  const { mode } = useAuth();
  const navigate = useNavigate();

  // Show loading state while fetching policy
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Check access - support both single key and array of keys
  const hasAccess = Array.isArray(moduleKey) 
    ? isAnyModuleEnabled(moduleKey)
    : isModuleEnabled(moduleKey);

  // If access granted, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // If custom fallback provided, use it
  if (fallback) {
    return <>{fallback}</>;
  }

  // In Employee mode the reason this page is shut is the mode, not the person's
  // role - so say so, and offer the way out rather than a dead end. The API has
  // already refused the data; this is only the explanation.
  if (mode === 'employee') {
    return <EmployeeModeNotice />;
  }

  // Default Access Denied UI
  return (
    <div className={`flex items-center justify-center min-h-[60vh] p-4 ${isRTL ? 'rtl' : 'ltr'}`}>
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <ShieldAlert className="w-8 h-8 text-red-600" />
          </div>
          <CardTitle className="text-xl text-red-700">
            {isRTL ? 'الوصول مرفوض' : 'Access Denied'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            {isRTL 
              ? 'ليس لديك صلاحية للوصول إلى هذه الصفحة. يرجى التواصل مع مدير النظام إذا كنت تعتقد أن هذا خطأ.'
              : 'You do not have permission to access this page. Please contact your administrator if you believe this is an error.'}
          </p>
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard')}
            className="gap-2"
          >
            <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
            {isRTL ? 'العودة إلى لوحة التحكم' : 'Back to Dashboard'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModuleGuard;
